import { useMemo } from "react";

import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { useAppData } from "@/hooks/useAppData";

/**
 * Returns the IDs of apartments matching the active apartment-filter predicates.
 *
 * Reads `predicates: StatFilter[]` from `useApartmentFilters()` and the
 * `apartmentDetailsMap` (`Map<number, Apartment>`) from `useAppData()`.
 *
 * Return-value contract — read carefully:
 *
 *   - **`null`** when `predicates.length === 0`. This signals "no filter is
 *     active". Callers that pass these IDs to a query parameter should
 *     **omit** the parameter entirely (not send `apartmentIds=`), so the
 *     server keeps its default per-apartment / per-group / total-portfolio
 *     behaviour. Frontend filterers should treat `null` as "let everything
 *     through".
 *
 *   - **`[]`** (empty array) when `predicates.length > 0` but no apartment in
 *     the registry matches every predicate. This signals "filter is active,
 *     expect zero results". Callers should send an explicit empty list (or
 *     short-circuit to an empty result UI) — emphatically NOT collapse to
 *     `null`, which would silently widen the result set back to "no filter".
 *
 *   - **`number[]`** (non-empty) when `predicates.length > 0` and at least
 *     one apartment matches.
 *
 * Memoised on `[predicates, apartmentDetailsMap]`. Because both inputs come
 * from upstream `useMemo` calls (predicates from `useApartmentFilters`, the
 * map from `useAppData`), recomputation only happens on a real input change —
 * not on every parent re-render.
 */
export function useFilteredApartmentIds(): number[] | null {
    const { predicates } = useApartmentFilters();
    const { apartmentDetailsMap } = useAppData();

    return useMemo<number[] | null>(() => {
        if (predicates.length === 0) {
            return null;
        }
        const ids: number[] = [];
        apartmentDetailsMap.forEach((apartment, id) => {
            if (predicates.every((predicate) => predicate(apartment))) {
                ids.push(id);
            }
        });
        return ids;
    }, [predicates, apartmentDetailsMap]);
}

export default useFilteredApartmentIds;
